import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
    Activity,
    Server,
    Database,
    RefreshCw,
    Trash2,
    AlertCircle,
    CheckCircle,
    Clock,
    Monitor,
    Cpu,
    Settings,
    RotateCcw,
    BarChart3
} from "lucide-react";
// Icon for backup users action
import { Users } from "lucide-react";
import { UserCog } from "lucide-react";
import { maintenanceAPI, SystemInfoResponse, EnvironmentInfoResponse, BackupUsersResponse } from "../api/maintenanceApi";
import { DemoAccountSettings } from "./DemoAccountSettings";
import EmailQuotaSummaryCard from "./email/EmailQuotaSummaryCard";

const AdminDashboard: React.FC = () => {
    const [systemInfo, setSystemInfo] = useState<SystemInfoResponse | null>(null);
    const [environmentInfo, setEnvironmentInfo] = useState<EnvironmentInfoResponse | null>(null);
    const [backupUsers, setBackupUsers] = useState<BackupUsersResponse | null>(null);
    const [loading, setLoading] = useState<Record<string, boolean>>({});

    // Initial load
    useEffect(() => {
        handleSystemInfo();
        handleEnvironmentInfo();
    }, []);

    const setLoadingState = (key: string, isLoading: boolean) => {
        setLoading(prev => ({ ...prev, [key]: isLoading }));
    };

    const handleSystemInfo = async () => {
        setLoadingState('systemInfo', true);
        try {
            const response = await maintenanceAPI.getSystemInfo();
            setSystemInfo(response);
        } catch (error) {
            console.error('System info failed:', error);
        } finally {
            setLoadingState('systemInfo', false);
        }
    };

    const handleEnvironmentInfo = async () => {
        setLoadingState('environmentInfo', true);
        try {
            const response = await maintenanceAPI.getEnvironmentInfo();
            setEnvironmentInfo(response);
        } catch (error) {
            console.error('Environment info failed:', error);
        } finally {
            setLoadingState('environmentInfo', false);
        }
    };

    const handleClearCache = async () => {
        setLoadingState('clearCache', true);
        try {
            const response = await maintenanceAPI.clearCache();
            console.log('Cache clear result:', response);
            if (!response.success) {
                alert(`Cache Clear: ${response.message}`);
            } else {
                alert('Cache cleared successfully!');
            }
        } catch (error) {
            console.error('Clear cache failed:', error);
            alert('Failed to clear cache: ' + (error instanceof Error ? error.message : 'Unknown error'));
        } finally {
            setLoadingState('clearCache', false);
        }
    };

    const handleRestartServices = async () => {
        if (!confirm('Are you sure you want to restart services? This may cause temporary downtime.')) {
            return;
        }

        setLoadingState('restartServices', true);
        try {
            const response = await maintenanceAPI.restartServices();
            console.log('Service restart result:', response);
            if (!response.success) {
                alert(`Service Restart: ${response.message}`);
            } else {
                alert('Services restarted successfully!');
            }
        } catch (error) {
            console.error('Restart services failed:', error);
            alert('Failed to restart services: ' + (error instanceof Error ? error.message : 'Unknown error'));
        } finally {
            setLoadingState('restartServices', false);
        }
    };

    const handleBackupUsers = async () => {
        if (!confirm('Backup users to MongoDB now? This might take a while.')) {
            return;
        }

        setLoadingState('backupUsers', true);
        try {
            const response = await maintenanceAPI.backupUsers();
            setBackupUsers(response);

            // Check if we have structured backupStats
            if (response.success && response.data && response.data.backupStats) {
                const stats = response.data.backupStats;
                alert(`Users backed up successfully!\nTotal: ${stats.totalUsers}\nBacked up: ${stats.backedUpUsers}\nSkipped: ${stats.skippedUsers}${stats.errors && stats.errors.length > 0 ? `\nErrors: ${stats.errors.length}` : ''}`);
            }
            // Check if the message contains backup statistics (fallback for different API response format)
            else if (response.success && response.message && response.message.includes('backed up')) {
                alert(`Backup completed successfully!\n${response.message}`);
            }
            // Handle failure cases
            else {
                alert(`Backup failed: ${response.message || 'Unknown error'}`);
            }
        } catch (error) {
            console.error('Backup users error:', error);
            alert('Backup users failed: ' + (error instanceof Error ? error.message : 'Unknown error'));
        } finally {
            setLoadingState('backupUsers', false);
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'healthy':
            case 'connected':
                return 'bg-green-100 text-green-800 border-green-200';
            case 'degraded':
                return 'bg-yellow-100 text-yellow-800 border-yellow-200';
            case 'down':
            case 'disconnected':
                return 'bg-red-100 text-red-800 border-red-200';
            default:
                return 'bg-gray-100 text-gray-800 border-gray-200';
        }
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'healthy':
            case 'connected':
                return <CheckCircle className="w-4 h-4" />;
            case 'degraded':
                return <AlertCircle className="w-4 h-4" />;
            case 'down':
            case 'disconnected':
                return <AlertCircle className="w-4 h-4" />;
            default:
                return <Clock className="w-4 h-4" />;
        }
    };

    const formatUptime = (seconds: number) => {
        const days = Math.floor(seconds / 86400);
        const hours = Math.floor((seconds % 86400) / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);

        if (days > 0) {
            return `${days}d ${hours}h ${minutes}m`;
        } else if (hours > 0) {
            return `${hours}h ${minutes}m`;
        } else {
            return `${minutes}m`;
        }
    };

    const formatBytes = (bytes: number) => {
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
        if (bytes === 0) return '0 Bytes';
        const i = Math.floor(Math.log(bytes) / Math.log(1024));
        return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
    };

    return (
        <div className="flex-1 min-h-0 overflow-y-auto p-6 space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
                    <p className="text-gray-600">System maintenance and monitoring</p>
                </div>
            </div>

            {/* Main Content with Tabs */}
            <Tabs defaultValue="status" className="w-full">
                <TabsList className="grid w-full grid-cols-3">
                    <TabsTrigger value="status" className="flex items-center space-x-2">
                        <BarChart3 className="w-4 h-4" />
                        <span>Status & Monitoring</span>
                    </TabsTrigger>
                    <TabsTrigger value="actions" className="flex items-center space-x-2">
                        <Settings className="w-4 h-4" />
                        <span>Admin Actions</span>
                    </TabsTrigger>
                    <TabsTrigger value="demo" className="flex items-center space-x-2">
                        <UserCog className="w-4 h-4" />
                        <span>Demo account</span>
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="status" className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <EmailQuotaSummaryCard />
                    </div>
                    {/* System Status Overview */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
                        <Card>
                            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                <CardTitle className="text-sm font-medium">Memory Usage</CardTitle>
                                <Monitor className="h-4 w-4 text-muted-foreground" />
                            </CardHeader>
                            <CardContent>
                                {systemInfo?.data.memory ? (
                                    <div>
                                        <div className="text-2xl font-bold">
                                            {systemInfo.data.memory.percentage.toFixed(1)}%
                                        </div>
                                        <p className="text-xs text-muted-foreground">
                                            {formatBytes(systemInfo.data.memory.used)} / {formatBytes(systemInfo.data.memory.total)}
                                        </p>
                                    </div>
                                ) : (
                                    <div className="text-sm text-gray-500">Loading...</div>
                                )}
                            </CardContent>
                        </Card>

                    </div>


                    {/* System Information */}
                    {systemInfo && (
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center">
                                    <Cpu className="w-5 h-5 mr-2" />
                                    System Information
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                    <div>
                                        <h4 className="font-medium text-sm text-gray-700 mb-2">Runtime</h4>
                                        <p className="text-sm">Node.js {systemInfo.data.nodeVersion}</p>
                                        <p className="text-sm text-gray-600">{systemInfo.data.platform} ({systemInfo.data.architecture})</p>
                                    </div>
                                    <div>
                                        <h4 className="font-medium text-sm text-gray-700 mb-2">Memory</h4>
                                        <p className="text-sm">{formatBytes(systemInfo.data.memory.used)} used</p>
                                        <p className="text-sm text-gray-600">{formatBytes(systemInfo.data.memory.total)} total</p>
                                        <p className="text-xs text-gray-500">{systemInfo.data.memory.percentage.toFixed(1)}% usage</p>
                                    </div>
                                    <div>
                                        <h4 className="font-medium text-sm text-gray-700 mb-2">CPU</h4>
                                        <p className="text-sm">{systemInfo.data.cpu.usage.toFixed(1)}% usage</p>
                                        <p className="text-xs text-gray-500">{systemInfo.data.cpu.cores} cores</p>
                                        {systemInfo.data.cpu.loadAverage && systemInfo.data.cpu.loadAverage.length > 0 && (
                                            <p className="text-xs text-gray-500">Load: {systemInfo.data.cpu.loadAverage[0].toFixed(2)}</p>
                                        )}
                                    </div>
                                    <div>
                                        <h4 className="font-medium text-sm text-gray-700 mb-2">Uptime</h4>
                                        <p className="text-sm">{formatUptime(systemInfo.data.uptime.process)}</p>
                                        <p className="text-xs text-gray-500">Process uptime</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/* Environment & Endpoints Information */}
                    {environmentInfo && (
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center">
                                    <Server className="w-5 h-5 mr-2" />
                                    Environment & Endpoints
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-6">
                                    {/* Frontend & API */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div>
                                            <h4 className="font-medium text-sm text-gray-700 mb-3 flex items-center">
                                                <Monitor className="w-4 h-4 mr-2" />
                                                Frontend
                                            </h4>
                                            <div className="space-y-2 text-sm">
                                                <div className="flex justify-between">
                                                    <span className="text-gray-600">URL:</span>
                                                    <span className="font-mono text-blue-600">{environmentInfo.data.frontend.url}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-gray-600">Port:</span>
                                                    <span className="font-mono">{environmentInfo.data.frontend.port}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-gray-600">Environment:</span>
                                                    <Badge variant="outline" className="capitalize">
                                                        {environmentInfo.data.frontend.environment}
                                                    </Badge>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-gray-600">Version:</span>
                                                    <span className="font-mono">{environmentInfo.data.frontend.version}</span>
                                                </div>
                                            </div>
                                        </div>

                                        <div>
                                            <h4 className="font-medium text-sm text-gray-700 mb-3 flex items-center">
                                                <Server className="w-4 h-4 mr-2" />
                                                API Backend
                                            </h4>
                                            <div className="space-y-2 text-sm">
                                                <div className="flex justify-between">
                                                    <span className="text-gray-600">URL:</span>
                                                    <span className="font-mono text-blue-600">{environmentInfo.data.api.url}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-gray-600">Port:</span>
                                                    <span className="font-mono">{environmentInfo.data.api.port}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-gray-600">Environment:</span>
                                                    <Badge variant="outline" className="capitalize">
                                                        {environmentInfo.data.api.environment}
                                                    </Badge>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-gray-600">Version:</span>
                                                    <span className="font-mono">{environmentInfo.data.api.version}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Databases */}
                                    <div>
                                        <h4 className="font-medium text-sm text-gray-700 mb-3 flex items-center">
                                            <Database className="w-4 h-4 mr-2" />
                                            Database Connections
                                        </h4>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            {/* MongoDB */}
                                            <div className="border rounded-lg p-4">
                                                <div className="flex items-center justify-between mb-3">
                                                    <h5 className="font-medium text-sm">MongoDB</h5>
                                                    <Badge className={getStatusColor(environmentInfo.data.databases.mongodb.status)}>
                                                        {getStatusIcon(environmentInfo.data.databases.mongodb.status)}
                                                        <span className="ml-1 capitalize">{environmentInfo.data.databases.mongodb.status}</span>
                                                    </Badge>
                                                </div>
                                                <div className="space-y-1 text-xs">
                                                    <div className="flex justify-between">
                                                        <span className="text-gray-600">Host:</span>
                                                        <span className="font-mono">{environmentInfo.data.databases.mongodb.host}</span>
                                                    </div>
                                                    <div className="flex justify-between">
                                                        <span className="text-gray-600">Port:</span>
                                                        <span className="font-mono">{environmentInfo.data.databases.mongodb.port}</span>
                                                    </div>
                                                    <div className="flex justify-between">
                                                        <span className="text-gray-600">Database:</span>
                                                        <span className="font-mono">{environmentInfo.data.databases.mongodb.database}</span>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Firebase Auth */}
                                            <div className="border rounded-lg p-4">
                                                <div className="flex items-center justify-between mb-3">
                                                    <h5 className="font-medium text-sm">Firebase Auth</h5>
                                                    <Badge className={getStatusColor(environmentInfo.data.databases.firebaseAuth.status)}>
                                                        {getStatusIcon(environmentInfo.data.databases.firebaseAuth.status)}
                                                        <span className="ml-1 capitalize">{environmentInfo.data.databases.firebaseAuth.status}</span>
                                                    </Badge>
                                                </div>
                                                <div className="space-y-1 text-xs">
                                                    <div className="flex justify-between">
                                                        <span className="text-gray-600">Project ID:</span>
                                                        <span className="font-mono">{environmentInfo.data.databases.firebaseAuth.projectId}</span>
                                                    </div>
                                                    <div className="flex justify-between">
                                                        <span className="text-gray-600">Auth Domain:</span>
                                                        <span className="font-mono text-xs">{environmentInfo.data.databases.firebaseAuth.authDomain}</span>
                                                    </div>
                                                    {environmentInfo.data.databases.firebaseAuth.emulator?.enabled && (
                                                        <>
                                                            <div className="mt-2 pt-2 border-t">
                                                                <span className="text-orange-600 font-medium text-xs">Emulator Mode</span>
                                                            </div>
                                                            {environmentInfo.data.databases.firebaseAuth.emulator.authHost && (
                                                                <div className="flex justify-between">
                                                                    <span className="text-gray-600">Auth:</span>
                                                                    <span className="font-mono">{environmentInfo.data.databases.firebaseAuth.emulator.authHost}</span>
                                                                </div>
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Proxy Configuration */}
                                    <div>
                                        <h4 className="font-medium text-sm text-gray-700 mb-3 flex items-center">
                                            <RefreshCw className="w-4 h-4 mr-2" />
                                            Proxy Configuration
                                        </h4>
                                        <div className="space-y-2 text-sm">
                                            <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                                                <span className="text-gray-600">Collections:</span>
                                                <span className="font-mono text-xs">{environmentInfo.data.proxy.collections}</span>
                                            </div>
                                            <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                                                <span className="text-gray-600">API Docs:</span>
                                                <span className="font-mono text-xs">{environmentInfo.data.proxy.apiDocs}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    )}

                </TabsContent>

                <TabsContent value="actions" className="space-y-6">
                    {/* Maintenance Actions */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center">
                                <Settings className="w-5 h-5 mr-2" />
                                System Maintenance Actions
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <Button
                                        onClick={handleBackupUsers}
                                        disabled={loading.backupUsers}
                                        variant="outline"
                                        className="flex items-center justify-start h-12"
                                    >
                                        <Users className="w-4 h-4 mr-2" />
                                        <div className="text-left">
                                            <div className="font-medium">Backup Users to MongoDB</div>
                                            <div className="text-xs text-gray-500">Copy users from Firebase → MongoDB</div>
                                        </div>
                                    </Button>
                                </div>

                                <div className="border-t pt-4">
                                    <h4 className="font-medium text-sm text-gray-700 mb-3">Dangerous Actions</h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <Button
                                            onClick={handleClearCache}
                                            disabled={loading.clearCache}
                                            variant="outline"
                                            className="flex items-center justify-start h-12 border-orange-200 hover:border-orange-300"
                                        >
                                            <Trash2 className="w-4 h-4 mr-2 text-orange-600" />
                                            <div className="text-left">
                                                <div className="font-medium">Clear Cache</div>
                                                <div className="text-xs text-gray-500">Clear application caches</div>
                                            </div>
                                        </Button>

                                        <Button
                                            onClick={handleRestartServices}
                                            disabled={loading.restartServices}
                                            variant="destructive"
                                            className="flex items-center justify-start h-12"
                                        >
                                            <RotateCcw className="w-4 h-4 mr-2" />
                                            <div className="text-left">
                                                <div className="font-medium">Restart Services</div>
                                                <div className="text-xs text-gray-500">Restart backend services</div>
                                            </div>
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Backup Users Results */}
                    {backupUsers && (
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center">
                                    <Users className="w-5 h-5 mr-2" />
                                    Backup Users Results
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                {backupUsers.success && backupUsers.data && backupUsers.data.backupStats ? (
                                    <div className="space-y-3">
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                            <div className="text-center">
                                                <div className="text-2xl font-bold text-blue-600">{backupUsers.data.backupStats.totalUsers}</div>
                                                <div className="text-sm text-gray-600">Total Users</div>
                                            </div>
                                            <div className="text-center">
                                                <div className="text-2xl font-bold text-green-600">{backupUsers.data.backupStats.backedUpUsers}</div>
                                                <div className="text-sm text-gray-600">Backed Up</div>
                                            </div>
                                            <div className="text-center">
                                                <div className="text-2xl font-bold text-yellow-600">{backupUsers.data.backupStats.skippedUsers}</div>
                                                <div className="text-sm text-gray-600">Skipped</div>
                                            </div>
                                            <div className="text-center">
                                                <div className="text-2xl font-bold text-red-600">{backupUsers.data.backupStats.errors.length}</div>
                                                <div className="text-sm text-gray-600">Errors</div>
                                            </div>
                                        </div>
                                        {backupUsers.data.backupStats.errors.length > 0 && (
                                            <div className="mt-4">
                                                <h4 className="font-medium text-sm text-gray-700 mb-2">Errors:</h4>
                                                <div className="bg-red-50 border border-red-200 rounded p-3">
                                                    <ul className="text-sm text-red-700 space-y-1">
                                                        {backupUsers.data.backupStats.errors.map((error, index) => (
                                                            <li key={index}>• {error}</li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ) : backupUsers.success && backupUsers.message ? (
                                    <div className="space-y-3">
                                        <div className="bg-green-50 border border-green-200 rounded p-4">
                                            <div className="flex items-center">
                                                <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                                                <h4 className="font-medium text-green-800">Backup Completed Successfully</h4>
                                            </div>
                                            <p className="text-green-700 mt-2">{backupUsers.message}</p>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="text-red-600">
                                        <p className="font-medium">Backup Failed</p>
                                        <p className="text-sm">{backupUsers.message || 'Unknown error occurred'}</p>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    )}

                    {/* Action Results */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center">
                                <AlertCircle className="w-5 h-5 mr-2" />
                                Action Results
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-2 text-sm">
                                {Object.entries(loading).map(([key, isLoading]) => (
                                    isLoading && (
                                        <div key={key} className="flex items-center space-x-2 text-blue-600">
                                            <RefreshCw className="w-4 h-4 animate-spin" />
                                            <span>Executing {key}...</span>
                                        </div>
                                    )
                                ))}
                                {Object.values(loading).every(loading => !loading) && (
                                    <div className="text-gray-500 italic">No actions currently running</div>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="demo" className="space-y-6">
                    <DemoAccountSettings />
                </TabsContent>
            </Tabs>
        </div>
    );
};

export default AdminDashboard;
